import React, { useState, useEffect } from 'react'

import Modal, { ModalHead, ModalBody } from './modal'
import Button from './button'
import Heading from './heading'
import Row, { Slot } from './row'
import styles from '../component-styles/request-an-expert.module.scss'
import Input from './input'
import Tick from '../svgs/tick'
import DisplayError from './display-error'
import { contactUsForm } from '../api-client/setup-client'
import { getToken } from '../helpers'

const initialState = {
  emailCheckbox: false,
  phoneCheckbox: false,
  agreementCheckbox: false,
  email: '',
  phone: '',
  message: '',
  name: '',
}

interface Props {
  active: boolean
  toggleActive: () => any
}

export default function ContactUsModal({
  active,
  toggleActive,
}: Props): React.ReactElement {
  const [error, setError] = useState({ message: '', attempts: 0 })
  const [status, setStatus] = useState({ requested: false, success: false })
  const [form, setForm] = useState(initialState)

  const updateForm = (item: { [key: string]: string | boolean }) =>
    setForm((f) => ({ ...f, ...item }))

  const handleSubmit = async () => {
    setStatus((s) => ({
      ...s,
      requested: true,
    }))

    const res = await contactUsForm(form)

    if (res) {
      setStatus({
        requested: true,
        success: true,
      })

      setForm(initialState)

      toggleActive()
      return
    }

    setStatus({
      requested: true,
      success: false,
    })
  }

  useEffect(() => {
    if (active) {
      // @ts-ignore
      if (window.dataLayer && window.dataLayer.push) {
        const token = getToken()
        // @ts-ignore
        window.dataLayer.push({ event: 'click-request-an-expert', token })
      }
    }
  }, [active])

  if (!active) {
    return <></>
  }

  if (status.requested && !status.success) {
    return (
      <Modal active={active} toggleActive={toggleActive}>
        <ModalHead>
          <Heading type={3} align="left" className={styles.heading}>
            We can help you!
          </Heading>
          <Row>
            <Slot width={230}>
              <ul className={styles.ul}>
                <li>
                  <Tick />
                  Expert help
                </li>
                <li>
                  <Tick />
                  Competitive rates
                </li>
                <li>
                  <Tick />
                  Unbiased advice
                </li>
              </ul>
            </Slot>
            <Slot>
              <p>
                Our partner{' '}
                <a
                  href="mailto:support@uplifter.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mezzo Labs
                </a>{' '}
                is the
                {" UK's "}
                largest digital intelligence consultancy, which can help you
                with all things data. Tell us your challenges and we will aim to
                respond in 24/7 with a quote.
              </p>
              <p>
                Having technical issues with the Uplifter tool? Please email{' '}
                <a
                  href="mailto:support@uplifter.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  support@uplifter.ai
                </a>
                .
              </p>
            </Slot>
          </Row>
        </ModalHead>
        <ModalBody>
          <h2>We are currently experiencing some issues.</h2>
          <p>
            Please contact us by emailing{' '}
            <a
              href="mailto:support@uplifter.ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              support@uplifter.ai
            </a>
            .
          </p>
          <Row>
            <Slot width={210}>
              <Button
                type="hollowBorder"
                onClick={toggleActive}
                ariaLabel="close modal"
              >
                Close
              </Button>
            </Slot>
          </Row>
        </ModalBody>
      </Modal>
    )
  }

  return (
    <>
      <Modal active={active} toggleActive={toggleActive}>
        <ModalHead>
          <Heading type={3} align="left" className={styles.heading}>
            We can help you!
          </Heading>
          <Row>
            <Slot width={230}>
              <ul className={styles.ul}>
                <li>
                  <Tick />
                  Expert help
                </li>
                <li>
                  <Tick />
                  Competitive rates
                </li>
                <li>
                  <Tick />
                  Unbiased advice
                </li>
              </ul>
            </Slot>
            <Slot>
              <p>
                Our partner{' '}
                <a
                  href="mailto:support@uplifter.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mezzo Labs
                </a>{' '}
                is the
                {" UK's "}
                largest digital intelligence consultancy, which can help you
                with all things data. Tell us your challenges and we will aim to
                respond in 24/7 with a quote.
              </p>
              <p>
                Having technical issues with the Uplifter tool? Please email{' '}
                <a
                  href="mailto:support@uplifter.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  support@uplifter.ai
                </a>
                .
              </p>
            </Slot>
          </Row>
        </ModalHead>
        <ModalBody>
          <form
            className={styles.form}
            action="/api/v1/support/request-an-expert"
            method="POST"
            onSubmit={(e: React.SyntheticEvent): void => {
              e.preventDefault()
              if (!form.emailCheckbox && !form.phoneCheckbox) {
                setError({
                  attempts: error.attempts + 1,
                  message:
                    'Please select either a phone or email to be contacted by.',
                })
                return
              }

              if (form.emailCheckbox && !form.email) {
                setError({
                  attempts: error.attempts + 1,
                  message:
                    'Please fill in your email in order to be contacted.',
                })
                return
              }

              if (form.phoneCheckbox && !form.phone) {
                setError({
                  attempts: error.attempts + 1,
                  message:
                    'Please fill in your phone in order to be contacted.',
                })
                return
              }

              handleSubmit()
            }}
          >
            <DisplayError attempts={error.attempts}>
              <span style={{ color: '#fff' }}>{error.message}</span>
            </DisplayError>
            <Row>
              <Slot padding="20px 0 0 0">
                <p>Name:</p>
                <Input
                  type="name"
                  label="Name"
                  name="name-input"
                  value={form.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                    updateForm({ name: e.target.value })
                  }
                />
              </Slot>
            </Row>
            <Row>
              <Slot padding="20px 0 0 0">
                <p>Message:</p>
                <textarea
                  value={form.message}
                  onChange={(
                    e: React.ChangeEvent<HTMLTextAreaElement>,
                  ): void => {
                    const { value: val } = e.target as HTMLTextAreaElement
                    updateForm({ message: val })
                  }}
                  className={styles.textArea}
                  placeholder="I need help with... Technical support, Conversion rate optimization, Reporting, Insights, Training."
                />
              </Slot>
            </Row>
            <p>I would like to be contacted by:</p>
            <Row vAlign="center" style={{ paddingBottom: 40 }}>
              <Slot width={84}>
                <Input
                  type="checkbox"
                  label="Email"
                  name="email"
                  checked={form.emailCheckbox}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    const { checked } = e.target as HTMLInputElement
                    updateForm({ emailCheckbox: checked })
                  }}
                />
              </Slot>
              <Slot padding="0 10px 0 0">
                <Input
                  type="email"
                  label="Email"
                  name="email-entry"
                  value={form.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    const { value: val } = e.target as HTMLInputElement
                    updateForm({ email: val })
                  }}
                  required={form.emailCheckbox}
                />
              </Slot>
              <Slot width={100} padding="0 0px 0 10px">
                <Input
                  type="checkbox"
                  label="Phone"
                  name="phone"
                  checked={form.phoneCheckbox}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    const { checked } = e.target as HTMLInputElement
                    updateForm({ phoneCheckbox: checked })
                  }}
                />
              </Slot>
              <Slot>
                <Input
                  type="text"
                  label="Phone"
                  name="phone-entry"
                  value={form.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    const { value: val } = e.target as HTMLInputElement
                    updateForm({ phone: val })
                  }}
                  required={form.phoneCheckbox}
                />
              </Slot>
            </Row>
            <Row>
              <Slot padding="0 0 40px 0">
                <Input
                  type="checkbox"
                  label="I agree to share this information with Mezzo Labs and Uplifter so they can contact me with support."
                  name="agreement"
                  checked={form.agreementCheckbox}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    const { checked } = e.target as HTMLInputElement
                    updateForm({ agreementCheckbox: checked })
                  }}
                  required
                />
              </Slot>
            </Row>
            <Row>
              <Slot padding="0px 10px 0 0" width={220}>
                <Button submit>Submit</Button>
              </Slot>
              <Slot padding="0px 0px 0 10px" width={220}>
                <Button
                  type="hollowBorder"
                  onClick={toggleActive}
                  ariaLabel="close modal"
                >
                  Next time
                </Button>
              </Slot>
            </Row>
          </form>
        </ModalBody>
      </Modal>
    </>
  )
}
